import React, { useState } from 'react';

import { Container, Form, Row, Col, Button } from 'react-bootstrap';
import Select from 'react-select';
import { css, StyleSheet } from 'aphrodite';
import Swal from 'sweetalert2';

import { areYouSure } from 'lib/utils/toast';
import { backgroundGrey5 } from 'lib/css/colors';
import { DEFAULT_PAGE_SIZE } from 'lib/constants';
import useGet from 'lib/hooks/useGet';
import usePost from 'lib/hooks/usePost';
import {
  PRODUCT_IMAGES_GET_AI_SUGGESTED_PRODUCT_IMAGE_TAGS,
  PRODUCT_IMAGES_UPDATE_AI_SUGGESTED_PRODUCT_IMAGE_TAGS,
} from 'lib/networking/endpoints';
import ImageCategorySelector from 'components/image_tagger_2/ImageCategorySelector';
import ImageGrid2 from './ImageGrid2';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import PaginationComponent from 'components/shared/PaginationComponent';

function ImageTagger2View() {
  const [selectedImages, setSelectedImages] = useState([]);
  const [selectedImageCategory, setSelectedImageCategory] = useState(null);
  const [selectedImageState, setSelectedImageState] = useState({
    label: 'Not Reviewed',
    value: 'NOT_REVIEWED',
  });
  const [confidence, setConfidence] = useState(null);
  const [confidenceInput, setConfidenceInput] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [selectedThreshold, setSelectedThreshold] = useState(null);
  const categoryOptions = [
    {
      label: 'Not Reviewed',
      value: 'NOT_REVIEWED',
    },
    {
      label: 'Approved',
      value: 'APPROVED',
    },
    {
      label: 'Rejected',
      value: 'REJECTED',
    },
  ];

  const thresholdOptions = [
    {
      label: 'High',
      value: 'HIGH',
    },
    {
      label: 'Low',
      value: 'LOW',
    },
  ];

  const {
    data: { data: [...imageData] = [] } = {},
    loading,
    refetch: refetchImages,
  } = useGet(PRODUCT_IMAGES_GET_AI_SUGGESTED_PRODUCT_IMAGE_TAGS, {
    predicted_attribute_id: selectedImageCategory?.value,
    is_higher_than_threshold: selectedThreshold?.value,
    confidence: confidence,
    status: selectedImageState?.value || null,
    limit: itemsPerPage,
    offset: itemsPerPage * (currentPage - 1),
  });

  const { data: { count } = {} } = useGet(PRODUCT_IMAGES_GET_AI_SUGGESTED_PRODUCT_IMAGE_TAGS, {
    predicted_attribute_id: selectedImageCategory?.value,
    is_higher_than_threshold: selectedThreshold?.value,
    confidence: confidence,
    status: selectedImageState?.value || null,
    limit: itemsPerPage,
    offset: itemsPerPage * (currentPage - 1),
    count_only: true,
  });

  const { postData: updateImagesStatus, loading: updating } = usePost(
    PRODUCT_IMAGES_UPDATE_AI_SUGGESTED_PRODUCT_IMAGE_TAGS,
    () => {
      refetchImages();
      Swal.fire({
        title: 'Images Updated Successfully',
        icon: 'success',
        position: 'center',
      }).then(() => {
        setSelectedImages([]);
      });
    },
    () => {
      Swal.fire({
        title: 'Error Updating Images',
        icon: 'error',
        position: 'center',
      });
    },
  );

  const handleApprove = () => {
    if (selectedImages.length === 0) {
      Swal.fire({
        title: 'Please select at least one image',
        icon: 'warning',
        position: 'center',
      });
      return;
    }

    updateImagesStatus({ selected_image_ids: selectedImages, status: 'APPROVED' });
  };

  const handleReject = () => {
    if (selectedImages.length === 0) {
      Swal.fire({
        title: 'Please select at least one image',
        icon: 'warning',
        position: 'center',
      });
      return;
    }
    updateImagesStatus({ selected_image_ids: selectedImages, status: 'REJECTED' });
  };

  const handleConfidenceChange = e => {
    if (e.key === 'Enter' && confidenceInput.trim() !== '') {
      setConfidence(confidenceInput);
      refetchImages();
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Container fluid>
      <div>
        <h3 className="font-weight-bold">Image Tagger</h3>
        <Row>
          <Col md={4}>
            <div className="text-muted">
              {count > 0
                ? `Showing ${itemsPerPage * (currentPage - 1) + 1}-${Math.min(
                    itemsPerPage * currentPage,
                    count,
                  )} of ${count} logs`
                : 'No logs found'}
            </div>
            <PaginationComponent
              totalItems={count}
              itemsPerPage={itemsPerPage}
              setItemsPerPage={setItemsPerPage}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              defaultSize
            />
          </Col>
          <Col>
            <Form.Group>
              <Form.Label>Image State</Form.Label>
              <Select
                isDisabled={loading}
                isClearable
                isLoading={loading}
                value={selectedImageState}
                options={categoryOptions}
                onChange={option => setSelectedImageState(option || null)}
                placeholder={loading ? 'Loading...' : 'Select State ...'}
              />
            </Form.Group>
          </Col>
          <Col>
            <ImageCategorySelector
              selectedImageCategory={selectedImageCategory}
              setSelectedImageCategory={option => {
                setSelectedImageCategory(option);
                setCurrentPage(1);
              }}
            />
          </Col>
          <Col>
            <Form.Group>
              <Form.Label>Threshold</Form.Label>
              <Select
                isDisabled={loading}
                isClearable
                isLoading={loading}
                value={selectedThreshold}
                options={thresholdOptions}
                onChange={option => setSelectedThreshold(option || null)}
                placeholder={loading ? 'Loading...' : 'Select Threshold ...'}
              />
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Label>Confidence</Form.Label>
            <Form.Control
              type="text"
              value={confidenceInput}
              onChange={e => setConfidenceInput(e.target.value)}
              onKeyPress={handleConfidenceChange}
              placeholder="Confidence"
            />
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={4} className="d-flex">
            <div className="d-flex">
              <Button
                variant="success"
                size="sm"
                onClick={() => setSelectedImages(imageData.map(img => img.id) || [])}
                disabled={
                  !imageData ||
                  imageData.length === 0 ||
                  selectedImages.length === (imageData.length || 0)
                }
                className="mr-2"
              >
                Select All
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={() => setSelectedImages([])}
                disabled={!selectedImages || selectedImages.length === 0}
                className="mr-2"
              >
                Deselect All
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  areYouSure(
                    () => handleApprove(),
                    'Are you sure you want to approve the selected suggestions?',
                  );
                }}
                disabled={
                  updating || selectedImages.length === 0 || !imageData || imageData.length === 0
                }
                className="mr-2"
              >
                Approve Selected ({selectedImages.length})
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={() => {
                  areYouSure(
                    () => handleReject(),
                    'Are you sure you want to reject the selected suggestions?',
                  );
                }}
                disabled={
                  updating || selectedImages.length === 0 || !imageData || imageData.length === 0
                }
              >
                Reject Selected ({selectedImages.length})
              </Button>
            </div>
          </Col>
        </Row>
        {imageData && imageData.length > 0 ? (
          <div>
            <ImageGrid2
              images={imageData}
              selectedImages={selectedImages}
              setSelectedImages={setSelectedImages}
            />
          </div>
        ) : (
          <div className={css(styles.noImagesMessage)}>
            <h4>No images found</h4>
            <p className="text-muted">
              There are no unapproved images in the database. All images may have been approved
              already.
            </p>
          </div>
        )}
      </div>
    </Container>
  );
}

const styles = StyleSheet.create({
  gridWrapper: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-start',
    boxSizing: 'border-box',
    marginTop: '15px',
  },
  errorAlert: {
    width: '98%',
    margin: '0 auto 20px auto',
  },
  noImagesMessage: {
    textAlign: 'center',
    padding: '40px 20px',
    backgroundColor: backgroundGrey5,
    borderRadius: '8px',
    width: '98%',
    margin: '20px auto',
  },
});

export default ImageTagger2View;
